import { useState, useEffect, Fragment, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Form, Field } from 'react-final-form';
import { toast } from 'react-toastify';
import { Grid2, Typography, useMediaQuery, Divider, Button, Card, CardContent, IconButton, Tooltip } from '@mui/material';
import { Archive, Delete, Edit, Unarchive } from '@mui/icons-material';

import { Title, TextInput, CheckboxComponent as Checkbox, CircularIndeterminate, PDFViewer, UploadPDFModal, BasicTable, SelectComponent, AlertDialog, CopyButton } from '@utriper/core-ui';
import { CommissionType, DocStatus, ExperiencerDocs, BookingOrigin } from '@utriper/core';

import EditIcon from 'assets/icons/edit-white.svg';

import { Method } from 'core/api/fetch';
import { ExperiencerDocsService } from 'core/api/services/experiencerDocs';
import { useCosts } from 'core/contexts/cost';
import { useExperiencer } from 'core/contexts/experiencer';
import { useExperiencerDocs } from 'core/contexts/experiencerDocs';
import { useTranslation } from 'core/contexts/localization';

import { AppBar, CostForm, Layout, PaymentPlatformCheckList } from 'components';

import { useStyles } from './styles';
import CommissionForm from './components/CommissionForm';
import ApiKeyModal from './components/ApiKeyForm';

const ExperiencerPage = () => {
    const classes = useStyles();
    const history = useHistory();
    const isSmall = useMediaQuery((theme) => theme.breakpoints.down('md'));
    const { t: translate, i18n } = useTranslation();
    const i18nLang = i18n.language;

    const {
        loading,
        experiencer,
        getExperiencer,
        setExperiencer,
        // addExperiencer,
        updateExperiencer,

        // Commissions
        getCommissions,
        patchCommission,
        deleteCommission,
        addCommission,
        commissions,

        // Costs
        getCosts,
        postCost,
        patchCost,
        deleteCost,
        costs,
        loadingSubmit,

        // API Keys
        getApiKeys,
        postApiKey,
        deleteApiKey,
        apiKeys,
    } = useExperiencer();

    const { costColumns, prettifyCosts } = useCosts();

    const [canEdit, setCanEdit] = useState(false);
    const [experiencerData, setExperiencerData] = useState({});
    const [notificationWhatsapp, setNotificationWhatsapp] = useState(null);
    const [openUploadPDF, setOpenUploadPDF] = useState(false);
    const [selectedPDF, setSelectedPDF] = useState(null);
    const [openPDFViewer, setOpenPDFViewer] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [openForm, setOpenForm] = useState(false);
    const [openDeleteForm, setOpenDeleteForm] = useState(false);
    const [commissionToEdit, setCommissionToEdit] = useState(null);
    const [commissionToDelete, setCommissionToDelete] = useState(null);
    const [openCostForm, setOpenCostForm] = useState(false);
    const [openDeleteCostForm, setOpenDeleteCostForm] = useState(false);
    const [costToEdit, setCostToEdit] = useState(null);
    const [costId, setCostId] = useState(null);
    const [openDeleteApiKeyForm, setOpenDeleteApiKeyForm] = useState(false);
    const [apiKeyToDelete, setApiKeyToDelete] = useState(null);
    const [openApiKeyForm, setOpenApiKeyForm] = useState(false);

    // const locationParam = document.location.href.split('/');
    // const experiencerIndex = parseInt(locationParam[locationParam.length - 1]);
    const { id } = useParams();
    const experiencerIndex = parseInt(id, 10) || 0;

    const { experiencerDocs, getExperiencerDocs, updateExperiencerDoc, currentDocs } = useExperiencerDocs();

    const experiencerDocsTypes = useMemo(
        () =>
            ExperiencerDocs?.getIds().map((id) => {
                const translated = ExperiencerDocs.getTranslatedName(ExperiencerDocs.types, id, i18n.language);
                return { name: translated?.[0] ?? '', id };
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [ExperiencerDocs, i18n.language]
    );
    const statusOptions = useMemo(
        () =>
            DocStatus?.getIds().map((id) => {
                const translated = DocStatus.getTranslatedName(DocStatus.status, id, i18n.language);
                return { name: translated?.[0] ?? '', id };
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [DocStatus, i18n.language]
    );
    const onSubmit = async (values) => {
        try {
            if (experiencerIndex === 0) {
                history.push(`/experiencer`);
            } else {
                const valuesClone = { ...values };
                valuesClone.notificationWhatsapp = notificationWhatsapp;
                valuesClone.viatorSupplierReference = parseInt(values.viatorSupplierReference) || null;
                valuesClone.gygSupplierReference = values.gygSupplierReference || null;
                valuesClone.addressStreet = values.addressStreet || null;
                valuesClone.addressCity = values.addressCity || null;
                valuesClone.addressCountry = values.addressCountry || null;
                valuesClone.description = values.description || null;
                await updateExperiencer(valuesClone);
                setCanEdit(false);
                getExperiencer(experiencerIndex);
            }
        } catch (err) {
            toast.error(err, {
                position: 'top-right',
                theme: 'colored',
            });
        }
    };
    const isPaymentPlatformChecked = (id) => {
        if (!experiencerData.acceptedPaymentPlatforms || !experiencerData.acceptedPaymentPlatforms.length) {
            return false;
        }
        return experiencerData.acceptedPaymentPlatforms.some((method) => method.id === id);
    };

    const onCheckPaymentPlatform = (value, paymentId) => {
        setExperiencerData((prev) => {
            const platforms = Array.isArray(prev.acceptedPaymentPlatforms) ? [...prev.acceptedPaymentPlatforms] : [];

            if (!value) {
                return {
                    ...prev,
                    acceptedPaymentPlatforms: platforms.filter((method) => method.id !== paymentId),
                };
            }

            return {
                ...prev,
                acceptedPaymentPlatforms: [...platforms, { id: paymentId }],
            };
        });
    };

    const columns = [
        { val: 'id', label: 'Id', column: 'ci_id' },
        { val: 'bookingOrigin', label: translate('experiencer.commissionColumns.bookingOrigin'), column: 'ci_booking_origin_id' },
        { val: 'feeType', label: translate('experiencer.commissionColumns.feeType'), column: 'ci_fee_type' },
        { val: 'feeRate', label: translate('experiencer.commissionColumns.feeRate'), column: 'ci_fee_rate' },
        { val: 'currency', label: translate('experiencer.commissionColumns.currency'), column: 'ci_currency' },
        { val: 'edit', label: ' ', style: { width: '1px' } },
        { val: 'delete', label: ' ', style: { width: '1px' } },
    ];

    const apiKeysColumns = [
        { val: 'name', label: 'API Key Name' },
        { align: 'center', val: 'keyPrefix', label: 'Key Prefix' },
        { val: 'resellerId', label: 'Reseller ID' },
        { val: 'isActive', label: 'Is Active' },
        { val: 'lastUsed', label: 'Last Used' },
        { val: 'delete', label: ' ', style: { width: '1px' } },
    ];

    const prettifyCommissions = (commissions = []) => {
        return commissions.map((commission) => ({
            ...commission,
            bookingOrigin: BookingOrigin.getTranslatedName(BookingOrigin.types, commission.bookingOriginId, i18nLang),
            feeType: `${CommissionType.getTranslatedName(CommissionType.types, commission?.feeType, i18n.language)}`,
            edit: (
                <Edit
                    onClick={() => {
                        setOpenForm(true);
                        setCommissionToEdit(commission);
                    }}
                    className={classes.tableButton}
                />
            ),
            delete: (
                <Delete
                    onClick={() => {
                        setOpenDeleteForm(true);
                        setCommissionToDelete(commission);
                    }}
                    className={classes.tableButton}
                />
            ),
        }));
    };

    const prettifyApiKeys = (apiKeys = []) => {
        return apiKeys.map((apiKey) => ({
            ...apiKey,
            keyPrefix: (
                <Grid2 container direction="row" alignItems="center" justifyContent="space-around" spacing={1}>
                    <Grid2 item>{apiKey.keyPrefix}</Grid2>
                    <Grid2 item>
                        <CopyButton defaultLabel="" textToCopy={apiKey.keyId} message="Copied to clipboard!" copiedLabel="Copied!" />
                    </Grid2>
                </Grid2>
            ),
            isActive: apiKey.isActive ? 'Yes' : 'No',
            lastUsed: apiKey.lastUsed ? new Date(apiKey.lastUsed).toLocaleDateString() : 'Never',
            delete: (
                <Delete
                    onClick={() => {
                        setOpenDeleteApiKeyForm(true);
                        setApiKeyToDelete(apiKey);
                    }}
                    className={classes.tableButton}
                />
            ),
        }));
    };

    const handleCloseForm = () => {
        setCommissionToEdit(null);
        setOpenForm(false);
    };

    const handleCloseDeleteForm = () => {
        setCommissionToDelete(null);
        setOpenDeleteForm(false);
    };

    const handleClickEditCost = (costId, cost) => {
        setOpenCostForm(true);
        setCostToEdit(cost);
        setCostId(costId);
    };

    const handleClickDeleteCost = (costId) => {
        setCostId(costId);
        setOpenDeleteCostForm(true);
    };

    const handleCloseCostForm = () => {
        setOpenCostForm(false);
        setCostId(null);
        setCostToEdit(null);
    };

    const handleCloseDeleteCost = () => {
        setOpenDeleteCostForm(false);
        setCostId(null);
    };

    const handleSubmitDeleteCost = async () => {
        if (costId) {
            await deleteCost(costId);
        }
        setCostId(null);
        setOpenDeleteCostForm(false);
        await getCosts(experiencerIndex);
    };

    const handleCloseDeleteApiKey = () => {
        setApiKeyToDelete(null);
        setOpenDeleteApiKeyForm(false);
    };

    const handleSubmitDeleteApiKey = async () => {
        toast.info('API Key deletion is not yet implemented');
        // if (apiKeyToDelete) {
        //     await deleteApiKey(experiencerIndex, apiKeyToDelete.id);
        //     setApiKeyToDelete(null);
        //     setOpenDeleteApiKeyForm(false);
        //     await getApiKeys(experiencerIndex);
        // }
    };

    const handleSubmitApiKey = async (values) => {
        try {
            const body = { ...values, userId: experiencer.userId };
            await postApiKey(experiencerIndex, body);
            toast.success(translate('experiencer.apiKey.success') || 'API Key created successfully', {
                position: 'top-right',
                theme: 'colored',
            });
            setOpenApiKeyForm(false);
            await getApiKeys(experiencerIndex);
        } catch (error) {
            toast.error(error.message || translate('experiencer.apiKey.errors.createFailed') || 'Failed to create API key', {
                position: 'top-right',
                theme: 'colored',
            });
            throw error;
        }
    };

    const onSubmitCommission = async (values) => {
        if (commissionToEdit) {
            await patchCommission(experiencerIndex, commissionToEdit.id, values);
            setCommissionToEdit(null);
        } else {
            await addCommission(experiencerIndex, values);
        }
        setOpenForm(false);
        await getCommissions(experiencerIndex);
    };

    const onSubmitDeleteCommission = async () => {
        if (commissionToDelete) {
            await deleteCommission(experiencerIndex, commissionToDelete.id);
            setCommissionToDelete(null);
            setOpenDeleteForm(false);
            await getCommissions(experiencerIndex);
        }
    };

    useEffect(() => {
        setExperiencer({});
        if (experiencerIndex === 0) {
            // setCanEdit(true);
            history.push(`/experiencer`);
        } else {
            getExperiencer(experiencerIndex);
            getExperiencerDocs(experiencerIndex);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setExperiencerData(experiencer);
        setNotificationWhatsapp(experiencer.notificationWhatsapp || false);
    }, [experiencer]);

    const handleOpenUploadPDF = () => setOpenUploadPDF(true);
    const handleCloseUploadPDF = () => setOpenUploadPDF(false);

    const onSubmitPDF = async (values) => {
        try {
            const result = await ExperiencerDocsService.savePDF(experiencerIndex, values);

            if (result) {
                toast.success(translate('experience_terms_page.success_new_policies'), {
                    position: 'top-right',
                    theme: 'colored',
                });
            } else {
                throw new Error(translate('experience_terms_page.error_new_policies'));
            }

            setOpenUploadPDF(false);
            getExperiencerDocs(experiencerIndex);
        } catch (error) {
            toast.error(error, {
                position: 'top-right',
                theme: 'colored',
            });
        }
    };

    const expDocsColumns = [
        {
            align: 'left',
            val: 'id',
            label: 'Id',
        },
        {
            val: 'name',
            label: 'Experiencer Doc Name',
        },
        {
            align: 'left',
            val: 'preview',
            label: 'Preview',
        },
        {
            val: 'version',
            label: 'Version',
        },
        {
            val: 'typeId',
            label: 'Type',
        },
        {
            val: 'expiryDate',
            label: 'Expiry Date',
        },
        {
            val: 'isCurrentVersion',
            label: 'Is Current Version',
            align: 'left',
        },
        {
            val: 'toggleArchive',
            label: 'Archivar',
            align: 'left',
        },
    ];

    const handleToggleArchive = async (doc) => {
        try {
            const nuevoStatus = doc.docStatus === DocStatus.IDS.live ? DocStatus.IDS.archived : DocStatus.IDS.live;

            const success = await updateExperiencerDoc(experiencerIndex, doc.id, {
                docStatus: nuevoStatus,
            });
            if (!success) {
                throw new Error('No se pudo actualizar el Status');
            }
        } catch (error) {
            toast.error('Error actualizando estado de documento');
        }
    };

    const handleSetActiveExperienceDocs = async (selectedId) => {
        try {
            const selectedDoc = experiencerDocs.find((d) => d.id === selectedId);
            if (!selectedDoc) return;

            const { typeId } = selectedDoc;

            const toArchive = currentDocs.filter((d) => d.typeId === typeId && d.id !== selectedId);
            for (const doc of toArchive) {
                await updateExperiencerDoc(experiencerIndex, doc.id, {
                    docStatus: DocStatus.IDS.archived,
                });
            }

            await updateExperiencerDoc(experiencerIndex, selectedId, {
                docStatus: DocStatus.IDS.live,
            });

            getExperiencer(experiencerIndex);
            getExperiencerDocs(experiencerIndex);
        } catch (error) {
            toast.error('Error actualizando versión activa');
        }
    };

    const handleListItemClick = (expDocs) => {
        const selected = experiencerDocs.find((docs) => docs.id === expDocs.id);
        setSelectedPDF(selected);
        setPageNumber(1);
    };

    const prettifyExperiencerDocs = (docs = []) => {
        const activeIds = new Set((currentDocs || []).filter((d) => d.docStatus === DocStatus.IDS.live).map((d) => d.id));

        return docs.map((experiencerDoc) => {
            let formattedExpiry = '';
            if (experiencerDoc.expiryDate) {
                const d = new Date(experiencerDoc.expiryDate);
                formattedExpiry = d.toLocaleDateString();
            }

            return {
                ...experiencerDoc,

                expiryDate: formattedExpiry,

                typeId: experiencerDoc.typeId ? ExperiencerDocs.getTranslatedName(ExperiencerDocs.types, experiencerDoc.typeId, i18n.language)?.[0] : '',

                preview: (
                    <Button
                        onClick={() => {
                            handleListItemClick(experiencerDoc);
                            setOpenPDFViewer(true);
                        }}
                        variant="text"
                        style={{
                            color: '#1976d2',
                            textTransform: 'none',
                            padding: 0,
                            minWidth: 'auto',
                            fontSize: '14px',
                            textDecoration: 'underline',
                        }}>
                        Ver Preview
                    </Button>
                ),
                isCurrentVersion: (
                    <div
                        onClick={() => handleSetActiveExperienceDocs(experiencerDoc.id)}
                        style={{
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            backgroundColor: activeIds.has(experiencerDoc.id) ? '#4caf50' : '#ccc',
                            cursor: 'pointer',
                            margin: 'auto',
                        }}
                    />
                ),
                toggleArchive: (
                    <Tooltip placement="right" title={experiencerDoc.docStatus === DocStatus.IDS.live ? translate('experiencer.activeBtn') : translate('experiencer.archiveBtn')}>
                        <IconButton size="small" onClick={() => handleToggleArchive(experiencerDoc)}>
                            {experiencerDoc.docStatus === DocStatus.IDS.live ? <Archive fontSize="medium" /> : <Unarchive fontSize="medium" />}
                        </IconButton>
                    </Tooltip>
                ),
            };
        });
    };

    useEffect(() => {
        if (experiencerIndex !== 0) {
            getCommissions(experiencerIndex);
            getCosts(experiencerIndex);
            getApiKeys(experiencerIndex);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [experiencerIndex]);

    return (
        <Layout contentClassName={classes.content}>
            <Title value={'Experiencer'} />
            {openPDFViewer && (
                <div
                    onClick={() => setOpenPDFViewer(false)}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '98vh',
                        padding: '10px',
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1300,
                    }}>
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            width: '80%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'white',
                            borderRadius: '8px',
                        }}>
                        <PDFViewer fileUrl={`${process.env.REACT_APP_CDN_URL}${selectedPDF?.docURL}`} fileName={selectedPDF?.name} pageNumber={pageNumber} setPageNumber={setPageNumber} />
                    </div>
                </div>
            )}
            <div style={{ paddingBottom: '1rem' }}>
                {loading ? (
                    <CircularIndeterminate />
                ) : (
                    <Fragment>
                        {/* STARTS EXPERIENCER INFO */}
                        <Grid2>
                            <div className={classes.formContainer}>
                                <Title value={experiencer.name} size={'l'} />
                                <Form
                                    onSubmit={onSubmit}
                                    initialValues={{
                                        ...experiencerData,
                                    }}
                                    render={({ handleSubmit, form }) => (
                                        <form id="form" onSubmit={handleSubmit}>
                                            <div>
                                                <div className={classes.divEditButton}>
                                                    <Typography
                                                        style={{
                                                            fontWeight: 'bold',
                                                            marginBottom: '10px',
                                                        }}
                                                        variant="h5"
                                                        className={classes.text}>
                                                        {translate('experiencer.edit_experiencer_data')}
                                                    </Typography>
                                                    <Button
                                                        type="button"
                                                        onClick={() => {
                                                            setCanEdit(true);
                                                        }}
                                                        style={{
                                                            height: 40,
                                                            margin: 'inherit',
                                                        }}
                                                        className={classes.editButton}>
                                                        <img src={EditIcon} width={20} alt="Edit Icon" />
                                                        {isSmall ? null : <>&nbsp;</>}
                                                        {isSmall ? null : `${translate('experiencer.edit')}`}
                                                    </Button>
                                                </div>

                                                <Divider />

                                                <div
                                                    style={{
                                                        marginTop: '20px',
                                                        marginBottom: '20px',
                                                    }}>
                                                    <Grid2 container spacing={3}>
                                                        <Grid2 size={{ xs: 12, sm: 3 }}>
                                                            {experiencer.pictureURL ? (
                                                                <>
                                                                    <Card
                                                                        style={{
                                                                            // minWidth: 275,
                                                                            maxWidth: 375,
                                                                        }}>
                                                                        <CardContent>
                                                                            <img
                                                                                src={`${process.env.REACT_APP_CDN_URL}${experiencer.pictureURL}`}
                                                                                style={{
                                                                                    width: '100%',
                                                                                }}
                                                                                alt={`Tour Operator ${experiencer?.name || ''}`}
                                                                            />
                                                                        </CardContent>
                                                                    </Card>
                                                                    <br />
                                                                </>
                                                            ) : null}
                                                        </Grid2>
                                                        <Grid2 size={{ xs: 12, sm: 9 }}>
                                                            <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                                <Grid2 container direction={'row'} justifyContent={'center'} alignItems={'center'} spacing={3}>
                                                                    <Grid2 size={{ xs: 12, sm: 6 }}>
                                                                        <Field id="id" name="id" component={TextInput} hintText={translate('experiencer.id')} label={translate('experiencer.id')} disabled />
                                                                    </Grid2>
                                                                    <Grid2 size={{ xs: 12, sm: 6 }}>
                                                                        <Field name="userId" id="userId" component={TextInput} hintText={translate('experiencer.userId')} label={translate('experiencer.userId')} disabled={true} />
                                                                    </Grid2>
                                                                </Grid2>
                                                                <Grid2 container direction={'row'} justifyContent={'center'} alignItems={'center'} spacing={3}>
                                                                    <Grid2 size={{ xs: 12, sm: 6 }}>
                                                                        <Field id="name" name="name" component={TextInput} hintText={translate('experiencer.name')} label={translate('experiencer.name')} disabled={!canEdit} />
                                                                    </Grid2>
                                                                    <Grid2 size={{ xs: 12, sm: 6 }}>
                                                                        <Field name="email" id="email" component={TextInput} hintText={translate('experiencer.email')} label={translate('experiencer.email')} disabled={!canEdit} />
                                                                    </Grid2>
                                                                </Grid2>
                                                                <Grid2 container direction={'row'} justifyContent={'center'} alignItems={'center'} spacing={3}>
                                                                    <Grid2 size={{ xs: 12, sm: 6 }}>
                                                                        <Field id="mobilePhone" name="mobilePhone" component={TextInput} hintText={translate('experiencer.mobilePhone')} label={translate('experiencer.mobilePhone')} disabled={!canEdit} />
                                                                    </Grid2>
                                                                    <Grid2 size={{ xs: 12, sm: 6 }}>
                                                                        <Field id="addressStreet" name="addressStreet" component={TextInput} hintText={translate('experiencer.addressStreet')} label={translate('experiencer.addressStreet')} disabled={!canEdit} />
                                                                    </Grid2>
                                                                </Grid2>
                                                                <Grid2 container direction={'row'} justifyContent={'center'} alignItems={'center'} spacing={3}>
                                                                    <Grid2 size={{ xs: 12, sm: 6 }}>
                                                                        <Field id="addressCity" name="addressCity" component={TextInput} hintText={translate('experiencer.addressCity')} label={translate('experiencer.addressCity')} disabled={!canEdit} />
                                                                    </Grid2>
                                                                    <Grid2 size={{ xs: 12, sm: 6 }}>
                                                                        <Field id="addressCountry" name="addressCountry" component={TextInput} hintText={translate('experiencer.addressCountry')} label={translate('experiencer.addressCountry')} disabled={!canEdit} />
                                                                    </Grid2>
                                                                </Grid2>
                                                                <Grid2 container direction={'row'} justifyContent={'center'} alignItems={'center'} spacing={3}>
                                                                    <Grid2 size={{ xs: 12, sm: 6 }}>
                                                                        <Field id="legalName" name="legalName" component={TextInput} hintText={translate('experiencer.legalName')} label={translate('experiencer.legalName')} disabled={!canEdit} />
                                                                    </Grid2>
                                                                    <Grid2 size={{ xs: 12, sm: 6 }}>
                                                                        <Field id="createdOn" name="createdOn" component={TextInput} hintText={translate('experiencer.createdOn')} label={translate('experiencer.createdOn')} disabled />
                                                                    </Grid2>
                                                                </Grid2>
                                                                <Grid2 container direction={'row'} justifyContent={'center'} alignItems={'center'} spacing={3}>
                                                                    <Grid2 size={{ xs: 12, sm: 6 }}>
                                                                        <Field name="viatorSupplierReference" id="viatorSupplierReference" component={TextInput} hintText={translate('experiencer.viatorSupplierReference')} label={translate('experiencer.viatorSupplierReference')} disabled={!canEdit} />
                                                                    </Grid2>
                                                                    <Grid2 size={{ xs: 12, sm: 6 }}>
                                                                        <Field name="viatorSupplierApiKey" id="viatorSupplierApiKey" component={TextInput} hintText={translate('experiencer.viatorSupplierApiKey')} label={translate('experiencer.viatorSupplierApiKey')} disabled={!canEdit} />
                                                                    </Grid2>
                                                                </Grid2>
                                                                <Grid2 container direction={'row'} justifyContent={'flex-end'} alignItems={'center'} spacing={3}>
                                                                    <Grid2 size={{ xs: 12, sm: 6 }}>
                                                                        <Field name="octoSupplierReference" id="octoSupplierReference" component={TextInput} hintText={translate('experiencer.octoSupplierReference')} label={translate('experiencer.octoSupplierReference')} disabled={!canEdit} />
                                                                    </Grid2>
                                                                    <Grid2 size={{ xs: 12, sm: 6 }}>
                                                                        <Field name="octoSupplierApiKey" id="octoSupplierApiKey" component={TextInput} hintText={translate('experiencer.octoSupplierApiKey')} label={translate('experiencer.octoSupplierApiKey')} disabled={!canEdit} />
                                                                    </Grid2>
                                                                </Grid2>
                                                                <Grid2 container direction={'row'} justifyContent={'flex-start'} alignItems={'center'} spacing={3}>
                                                                    <Grid2 size={{ xs: 12, sm: 6 }}>
                                                                        <Field name="gygSupplierReference" id="gygSupplierReference" component={TextInput} hintText={translate('experiencer.gygSupplierReference')} label={translate('experiencer.gygSupplierReference')} disabled={!canEdit} />
                                                                    </Grid2>
                                                                </Grid2>
                                                            </div>
                                                            <Grid2 container justifyContent={'center'} alignItems={'center'} spacing={3}>
                                                                <Grid2 size={{ xs: 12, sm: 4 }}>
                                                                    <PaymentPlatformCheckList isTourOperator={true} canEdit={canEdit} checked={isPaymentPlatformChecked} onChange={onCheckPaymentPlatform} />
                                                                </Grid2>
                                                                <Grid2 size={{ xs: 12, sm: 8 }}>
                                                                    <Grid2 container justifyContent={'center'} alignItems={'center'} spacing={3}>
                                                                        <Grid2 size={{ xs: 12 }}>
                                                                            <Field
                                                                                style={{
                                                                                    paddingTop: '0',
                                                                                }}
                                                                                disabled={!canEdit}
                                                                                checked={notificationWhatsapp}
                                                                                onChange={(value) => {
                                                                                    setNotificationWhatsapp(value);
                                                                                }}
                                                                                name="notificationWhatsapp"
                                                                                component={Checkbox}
                                                                                labelComponent={translate('experiencer.notificationWhatsapp')}
                                                                            />
                                                                        </Grid2>
                                                                        <Grid2 size={{ xs: 12 }}></Grid2>
                                                                        <Field
                                                                            style={{
                                                                                // marginBottom:
                                                                                //     '2em',
                                                                                width: '99%',
                                                                            }}
                                                                            id="description"
                                                                            name="description"
                                                                            component={'textarea'}
                                                                            label={translate('experiencer.description')}
                                                                            disabled={!canEdit}
                                                                            multiline
                                                                            rows={8}
                                                                            // initialCharacters={contract?.description?.length || 500}
                                                                        />
                                                                    </Grid2>
                                                                </Grid2>
                                                            </Grid2>
                                                        </Grid2>
                                                    </Grid2>
                                                </div>
                                            </div>

                                            <Grid2 container justifyContent="flex-end" alignContent="flex-end">
                                                <Grid2 container justifyContent={'space-between'} style={{ width: 'auto' }}>
                                                    <Button
                                                        className="rounded mr-3 height-40 w-100px"
                                                        disabled={!canEdit}
                                                        type="button"
                                                        onClick={(e) => {
                                                            setCanEdit(false);
                                                            return form.reset(e);
                                                        }}>
                                                        {translate('btn_cancel')}
                                                    </Button>

                                                    <Button
                                                        onClick={(e) => {
                                                            setCanEdit(false);
                                                            return handleSubmit(e);
                                                        }}
                                                        className="rounded height-40 w-100px triper-button bg-green-300"
                                                        disabled={!canEdit}
                                                        type="submit">
                                                        {translate('btn_submit')}
                                                    </Button>
                                                </Grid2>
                                            </Grid2>
                                        </form>
                                    )}
                                />
                            </div>
                        </Grid2>

                        {/* API Keys Table */}
                        {experiencer.id && (
                            <Grid2 container size={{ xs: 12, md: 6 }} flexDirection="column" style={{ marginTop: '20px' }}>
                                <Grid2 width="100%" className={classes.tableHeader}>
                                    <Title size="s" value="API Keys" />
                                    <Grid2>
                                        <Button
                                            variant="contained"
                                            onClick={() => {
                                                setOpenApiKeyForm(true);
                                            }}>
                                            {translate('btn_add')}
                                        </Button>
                                    </Grid2>
                                </Grid2>

                                <Grid2>
                                    <BasicTable columns={apiKeysColumns} values={prettifyApiKeys(apiKeys)} />
                                </Grid2>
                            </Grid2>
                        )}

                        {/* Legal Docs Table */}
                        {experiencer.id && (
                            <Grid2 container size={{ xs: 12, md: 6 }} flexDirection="column">
                                <Grid2 width="100%" className={classes.tableHeader}>
                                    <Title size="s" value="Experiencer Legal Docs" />
                                    <Grid2>
                                        <Button
                                            variant="contained"
                                            onClick={() => {
                                                handleOpenUploadPDF();
                                            }}>
                                            {translate('btn_add')}
                                        </Button>
                                    </Grid2>
                                </Grid2>

                                <Grid2>
                                    <BasicTable columns={expDocsColumns} values={prettifyExperiencerDocs(experiencerDocs)} />
                                </Grid2>
                            </Grid2>
                        )}

                        {experiencer?.id && (
                            <Grid2 container width="100%" justifyContent="space-between" alignItems="flex-start" mt={2} direction={{ xs: 'column', lg: 'row' }}>
                                {/* Commissions Table */}
                                <Grid2 container size={{ xs: 12, lg: 5.5 }} flexDirection="column">
                                    <Grid2 width="100%" className={classes.tableHeader}>
                                        <Title size="s" value={translate('experiencer.commissions')} />
                                        <Grid2>
                                            <Button
                                                variant="contained"
                                                onClick={() => {
                                                    setOpenForm(true);
                                                }}>
                                                {translate('btn_add')}
                                            </Button>
                                        </Grid2>
                                    </Grid2>

                                    <Grid2>
                                        <BasicTable columns={columns} values={prettifyCommissions(commissions)} />
                                    </Grid2>
                                </Grid2>

                                {/* Costs Table */}
                                <Grid2 container size={{ xs: 12, lg: 5.5 }} flexDirection="column">
                                    <Grid2 width="100%" className={classes.tableHeader}>
                                        <Title size="s" value={translate('experiencer.costs.title')} />
                                        <Grid2>
                                            <Button
                                                variant="contained"
                                                onClick={() => {
                                                    setOpenCostForm(true);
                                                }}>
                                                {translate('btn_add')}
                                            </Button>
                                        </Grid2>
                                    </Grid2>
                                    <Grid2>
                                        <BasicTable columns={costColumns(true)} values={prettifyCosts(costs, classes, handleClickEditCost, handleClickDeleteCost)} />
                                    </Grid2>
                                </Grid2>
                            </Grid2>
                        )}

                        {/* ENDS EXPERIENCER INFO */}

                        {/* Upload Legal Docs */}
                        <UploadPDFModal open={openUploadPDF} onClose={handleCloseUploadPDF} onSubmit={onSubmitPDF}>
                            <Grid2 container spacing={2}>
                                <Grid2 size={{ xs: 12, md: 6 }}>
                                    <Field name="version" component={TextInput} label="Versión" placeholder="Ej. 1.0" />
                                </Grid2>
                                <Grid2 size={{ xs: 12, md: 6 }}>
                                    <Field name="expiryDate" component={TextInput} type="date" label="Fecha de Expiración" InputLabelProps={{ shrink: true }} />
                                </Grid2>
                                <Grid2 size={{ xs: 12, md: 6 }}>
                                    <Field id="docStatus" name="docStatus" component={SelectComponent} values={statusOptions} label="Estado" labelId="status-label" style={{ width: '100%' }} />
                                </Grid2>
                                <Grid2 size={{ xs: 12, md: 6 }}>
                                    <Field id="typeId" name="typeId" component={SelectComponent} values={experiencerDocsTypes} label="Type" labelId="type-label" style={{ width: '100%' }} />
                                </Grid2>
                            </Grid2>
                        </UploadPDFModal>

                        {/* Commission Form */}
                        <AlertDialog open={openForm} dialogContent={<CommissionForm commission={commissionToEdit} onSubmit={onSubmitCommission} onClose={handleCloseForm} />} handleClose={handleCloseForm} hideTitle hideActions maxWidth={'sm'} fullWidth />

                        {/* Commission Delete */}
                        <AlertDialog open={openDeleteForm} dialogTitle={translate('experiencer.commissions')} dialogContent={`${translate('experiencer.deleteDialog.first')} ${BookingOrigin.getLabel(commissionToDelete?.bookingOriginId) ?? ''}? ${translate('experiencer.deleteDialog.second')}`} handleClose={handleCloseDeleteForm} handleSubmit={onSubmitDeleteCommission} />

                        {/* Cost Form */}
                        {experiencer?.id && (
                            <>
                                <AlertDialog
                                    open={openCostForm}
                                    dialogTitle={costId && costToEdit ? translate('edit_cost.title.edit') : translate('edit_cost.title.new')}
                                    dialogContent={
                                        <CostForm
                                            id={costId}
                                            initialCost={costToEdit}
                                            showOrigin={true}
                                            entity="experiencer"
                                            entityId={experiencer?.id}
                                            loading={loadingSubmit}
                                            fetchMethod={costId && costToEdit ? Method.PATCH : Method.POST}
                                            onClose={(isSubmit = false) => {
                                                handleCloseCostForm();
                                                if (isSubmit) {
                                                    getCosts(experiencerIndex);
                                                }
                                            }}
                                            postCost={postCost}
                                            updateCost={patchCost}
                                            showPaymentPlatform={true}
                                        />
                                    }
                                    handleClose={handleCloseCostForm}
                                    hideActions
                                    maxWidth="sm"
                                    fullWidth
                                />

                                <AlertDialog open={openDeleteCostForm} dialogTitle={translate('edit_cost.delete.title')} dialogContent={`${translate('edit_cost.delete.content')} ${costId}?`} handleClose={handleCloseDeleteCost} handleSubmit={handleSubmitDeleteCost} />
                            </>
                        )}

                        {/* API Key Create */}
                        <ApiKeyModal open={openApiKeyForm} onClose={() => setOpenApiKeyForm(false)} onSubmit={handleSubmitApiKey} loading={loadingSubmit} />

                        {/* API Key Delete */}
                        <AlertDialog open={openDeleteApiKeyForm} dialogTitle="Delete API Key" dialogContent={`Are you sure you want to delete the API key "${apiKeyToDelete?.name}"? This action cannot be undone.`} handleClose={handleCloseDeleteApiKey} handleSubmit={handleSubmitDeleteApiKey} />
                    </Fragment>
                )}
            </div>
        </Layout>
    );
};

ExperiencerPage.displayName = 'ExperiencerPage';

export default ExperiencerPage;
